// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import { useParties } from '../../../contexts/PartiesContext';
import useCreateParticipantOnboardingRequest from '../../../hooks/mutations/registry/onboarding/useCreateParticipantOnboardingRequest';
import useMutate from '../../../hooks/mutations/useMutate';
import { useConfig } from '../../../hooks/other/useConfig';
import useParticipantOnboardingRequests from '../../../hooks/queries/registry/onboarding/useParticipantOnboardingRequests';
import { useParticipantServices } from '../../../hooks/queries/registry/onboarding/useParticipantServices';
import {
  ERR_REQ_PARTICIPANT_ONBOARDING,
  SUC_REQ_PARTICIPANT_ONBOARDING,
} from '../../../utils/strings';

type DialogProps = {
  open: boolean;
  onClose: () => void;
};

export const RequestParticipantOnboardingDialog = ({ open, onClose }: DialogProps) => {
  const [operatorPartyId, setOperatorPartyId] = useState<string>('');
  const [compliancePartyId, setCompliancePartyId] = useState<string>('');
  const [clientOnboarderPartyId, setClientOnboarderPartyId] = useState<string>('');
  const [registrarPartyId, setRegistrarPartyId] = useState<string>('');
  const [upgraderPartyId, setUpgraderPartyId] = useState<string>('');
  const mutate = useMutate();
  const createParticipantOnboardingRequestMutation = useCreateParticipantOnboardingRequest();
  const { party } = useParties();
  const participantServices = useParticipantServices();
  const participantOnboardingRequests = useParticipantOnboardingRequests();
  const config = useConfig();

  const isLoadingValidationData =
    participantServices.isLoading || participantOnboardingRequests.isLoading;

  // Validation: check for existing service or pending request with the provided clientOnboarder
  const duplicateCheck = useMemo(() => {
    if (!clientOnboarderPartyId) return { isDuplicate: false, reason: '' };

    const hasExistingService = participantServices.data?.some(
      (svc) =>
        svc.payload.participant === party && svc.payload.clientOnboarder === clientOnboarderPartyId,
    );
    if (hasExistingService) {
      return {
        isDuplicate: true,
        reason: 'You already have an active ParticipantService with this Client Onboarder',
      };
    }

    const hasPendingRequest = participantOnboardingRequests.data?.some(
      (req) =>
        req.payload.participant === party && req.payload.clientOnboarder === clientOnboarderPartyId,
    );
    if (hasPendingRequest) {
      return {
        isDuplicate: true,
        reason: 'You already have a pending onboarding request with this Client Onboarder',
      };
    }

    return { isDuplicate: false, reason: '' };
  }, [clientOnboarderPartyId, party, participantServices.data, participantOnboardingRequests.data]);

  // Compute validation message for the client onboarder input field
  const inputFieldValidationMessage = useMemo(() => {
    if (duplicateCheck.isDuplicate) {
      return duplicateCheck.reason;
    }
    return undefined;
  }, [duplicateCheck]);

  const resetState = useCallback(() => {
    setOperatorPartyId('');
    setCompliancePartyId('');
    setClientOnboarderPartyId('');
    setRegistrarPartyId('');
    setUpgraderPartyId('');
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const requestParticipantOnboarding = useCallback(async () => {
    const expiration = new Date(
      new Date().getTime() + config.settings.onboarding_request_timeout_milliseconds,
    ).toISOString();
    const payload = {
      operator: operatorPartyId,
      compliance: compliancePartyId,
      registrar: registrarPartyId,
      clientOnboarder: clientOnboarderPartyId,
      participant: party,
      expiration,
      upgrader: upgraderPartyId,
    };

    await mutate(
      createParticipantOnboardingRequestMutation,
      payload,
      SUC_REQ_PARTICIPANT_ONBOARDING,
      ERR_REQ_PARTICIPANT_ONBOARDING,
    );
    handleClose();
  }, [
    operatorPartyId,
    compliancePartyId,
    registrarPartyId,
    clientOnboarderPartyId,
    upgraderPartyId,
    party,
    handleClose,
    mutate,
    createParticipantOnboardingRequestMutation,
    config.settings.onboarding_request_timeout_milliseconds,
  ]);

  const mutationIsPending = createParticipantOnboardingRequestMutation.isPending;
  const disableSubmit =
    !operatorPartyId ||
    !compliancePartyId ||
    !clientOnboarderPartyId ||
    !registrarPartyId ||
    !upgraderPartyId ||
    mutationIsPending ||
    isLoadingValidationData ||
    duplicateCheck.isDuplicate;

  return (
    <FormDialog
      open={open}
      title="Request Participant Onboarding"
      onCancel={handleClose}
      onSubmit={requestParticipantOnboarding}
      submitName="Request"
      isSubmitDisabled={disableSubmit}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextInput
          enableTopLabel
          label="Operator Party ID"
          value={operatorPartyId}
          setValue={setOperatorPartyId}
        />
        <TextInput
          enableTopLabel
          label="Compliance Party ID"
          value={compliancePartyId}
          setValue={setCompliancePartyId}
        />
        <TextInput
          enableTopLabel
          label="Client Onboarder Party ID"
          value={clientOnboarderPartyId}
          setValue={setClientOnboarderPartyId}
          validationMessage={inputFieldValidationMessage}
        />
        <TextInput
          enableTopLabel
          label="Registrar Party ID"
          value={registrarPartyId}
          setValue={setRegistrarPartyId}
        />
        <TextInput
          enableTopLabel
          label="Upgrader Party ID"
          value={upgraderPartyId}
          setValue={setUpgraderPartyId}
        />
      </Box>
    </FormDialog>
  );
};

export default RequestParticipantOnboardingDialog;
